// StaticHoldTimer — T-016 Part A / T-095 §3. Counts up to a target
// hold time, then the person can stop early via the Stop control.
// The held seconds are the set's value, not 0; Set-done is
// disabled while seconds < 1 (the hold equivalent of the
// zero-rep rule).

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";

export interface StaticHoldTimerProps {
  targetSeconds: number;
  /** Called when the person confirms a held value. The argument
   *  is the seconds to log — what the timer measured (or what
   *  ±1 corrected it to), not 0. */
  onConfirm: (seconds: number) => void;
  autoStart?: boolean;
  /** Localized tier label for the target line. */
  tierLabel?: string;
}

export function StaticHoldTimer({
  targetSeconds,
  onConfirm,
  autoStart = true,
  tierLabel,
}: StaticHoldTimerProps): JSX.Element {
  const { t } = useI18n();
  const [elapsed, setElapsed] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [reached, setReached] = useState(false);
  const startRef = useRef<number | null>(autoStart ? Date.now() : null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (startRef.current === null) return;
    const handle = window.setInterval(() => {
      const sec = Math.floor((Date.now() - (startRef.current as number)) / 1000);
      setElapsed(sec);
      if (sec >= targetSeconds && !completedRef.current) {
        completedRef.current = true;
        setReached(true);
        window.clearInterval(handle);
        // Auto-stop at target freezes the value; the person can
        // still ±1 and confirm.
        setStopped(true);
      }
    }, 250);
    return () => window.clearInterval(handle);
  }, [targetSeconds]);

  const fraction = Math.min(1, elapsed / Math.max(1, targetSeconds));

  const stop = () => {
    if (stopped) return;
    setStopped(true);
  };
  const inc = () => setElapsed((s) => s + 1);
  const dec = () => setElapsed((s) => Math.max(0, s - 1));
  const confirm = () => {
    if (elapsed < 1) return;
    onConfirm(elapsed);
  };

  return (
    <div
      className="flex flex-col items-center gap-4"
      data-testid="hold-timer"
      aria-live="polite"
    >
      <p
        data-testid="hold-elapsed"
        className="font-numeric tabular text-[var(--color-ink-hi)]"
        style={{ fontSize: 96, lineHeight: 1, fontWeight: 700 }}
      >
        {t("hold.elapsed", { elapsed })}
      </p>
      <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)]">
        {t("workoutRunner.targetHoldTier", {
          seconds: targetSeconds,
          tier: tierLabel ?? "",
        })}
      </p>
      <div
        className="bg-[var(--color-line)]"
        style={{ width: "min(80vw, 360px)", height: 6, borderRadius: "var(--radius-sharp)" }}
        role="progressbar"
        aria-valuenow={Math.round(fraction * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="bg-[var(--color-lime)]"
          style={{
            width: `${Math.round(fraction * 100)}%`,
            height: "100%",
            transition: "width 200ms linear",
          }}
        />
      </div>
      {reached && (
        <p
          data-testid="hold-reached"
          className="font-subhead text-base uppercase tracking-[0.06em] text-[var(--color-lime)]"
        >
          {t("hold.targetReached")}
        </p>
      )}
      {stopped ? (
        <>
          <p
            data-testid="hold-held"
            className="font-subhead text-base uppercase tracking-[0.06em] text-[var(--color-ink-hi)]"
          >
            {t("workoutRunner.hold.held", { seconds: elapsed })}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={dec}
              aria-label={t("workoutRunner.secondMinus")}
              data-testid="hold-dec"
              className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] font-display text-[var(--color-ink-hi)] hover:border-[var(--color-lime)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
              style={{ minHeight: "64px", minWidth: "64px", borderRadius: "var(--radius-sharp)", fontSize: 28 }}
            >
              −
            </button>
            <button
              type="button"
              onClick={inc}
              aria-label={t("workoutRunner.secondPlus")}
              data-testid="hold-inc"
              className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] font-display text-[var(--color-ink-hi)] hover:border-[var(--color-lime)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all"
              style={{ minHeight: "64px", minWidth: "64px", borderRadius: "var(--radius-sharp)", fontSize: 28 }}
            >
              +
            </button>
          </div>
          <button
            type="button"
            data-testid="hold-done"
            onClick={confirm}
            disabled={elapsed < 1}
            className="font-display uppercase tracking-[0.02em] text-[var(--color-void)] disabled:cursor-not-allowed disabled:opacity-40"
            style={{ minHeight: "96px", fontSize: 28, backgroundColor: "var(--color-lime)", borderRadius: "var(--radius-sharp)", padding: "0 24px", minWidth: "200px" }}
          >
            {t("workoutRunner.actions.done")}
          </button>
        </>
      ) : (
        <button
          type="button"
          data-testid="hold-stop"
          onClick={stop}
          className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:border-[var(--color-lime)] active:scale-95 transition-all"
          style={{ minHeight: "64px", minWidth: "200px", borderRadius: "var(--radius-sharp)" }}
        >
          {t("workoutRunner.hold.stop")}
        </button>
      )}
    </div>
  );
}